import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Theme, styled, useMediaQuery } from '@mui/material'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { MultimodalDownloadOption } from './ImageInfoDownload';
import {BiometricFaceData, BiometricFingerData, BiometricVoiceData, MultimodalSubject, Operation, Subject} from 'neurotec-megamatcherid-management-client';
import ImageCanvas from './ImageCanvas';
import { useSnackbar } from 'notistack';
import { APIsContext } from '../../Store/APIs/context';
import ConfirmationButton from '../Misc/Dialog/ConfirmationButton';
import DeleteIcon from '@mui/icons-material/Delete';
import OverviewInfo from './OverviewInfo';
import DialogTitleClose from '../Misc/Dialog/Components/DialogTitleClose';
import { NavigationContext } from '../../Store/Navigation/context';
import { setLoading, setSubjectUUID } from '../../Store/Navigation/reducer';
import ButtonLightGray from '../Filter/Components/ButtonLightGray';
import { useHistory } from 'react-router-dom';
import { OperationType } from '../../Store/Operations/types';
import { Status } from '../../Store/Subjects/types';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { SelectionIconButton } from './SelectionIconButton';
import { THEME } from '../../config';
import AudioOperationSummary from './AudioOperationSummary';
import BiometricDataDownload from './ImageInfoDownload';

const GridImage = styled(Grid)(({theme}) => ({
    minHeight: "5rem",
    paddingRight: "0.5rem",
    [theme.breakpoints.down("md")]: {
        marginBottom: "3rem",
        paddingRight: "0rem"
    }
}));

interface IOverviewDialogProps {
    multimodalSubject?: MultimodalSubject,
    singleOperation?: Operation,
    open: boolean,
    onClose: () => void,
    onImageDelete: () => void,
    onImageDownload: (id: string, downloadOptions: any) => void,
    isDeletable: boolean,
    isOperation?: boolean,
}

const OverviewDialog: React.FC<IOverviewDialogProps> = (props) => {

    const { enqueueSnackbar } = useSnackbar();
    const [faceInfo, setFaceInfo] = useState<BiometricFaceData | void | null>(undefined);
    const [voiceInfo, setVoiceInfo] = useState<BiometricVoiceData | void | null>(undefined)
    const [fingerInfo, setFingerInfo] = useState<(BiometricFingerData | undefined)[]>([])
    const [fingerIndex, setFingerIndex] = useState(0)
    const history = useHistory();
    const navigationDispatch = useContext(NavigationContext).dispatch
    const apisState = useContext(APIsContext).state
    const [operationList, setOperationList] = useState<Operation[]>([])
    const [activeModalityIndex, setActiveModalityIndex] = useState(0)
    const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const tokenizeOperation = useCallback(() => {
        let list: Operation[] = []
        if (props.singleOperation !== undefined) {
            list.push(props.singleOperation)
        }
        if (props.multimodalSubject !== undefined) {
            props.multimodalSubject.multimodalOperations?.forEach(o => list.push(o))
        }
        return list
    }, [props.multimodalSubject, props.singleOperation])

    const handleExited = () => {
        setFaceInfo(undefined);
        setVoiceInfo(undefined);
        setFingerInfo([])
    }

    useEffect(() => {
        setOperationList(tokenizeOperation());
        setActiveModalityIndex(0);
        setFingerIndex(0)
    }, [props.open, tokenizeOperation]);
    
    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            if (!operationList[activeModalityIndex]?.modality) return;
            navigationDispatch(setLoading(true));
            try {
                const { modality, uuid, fingers } = operationList[activeModalityIndex];
                if (!uuid) return;
                switch (modality) {
                    case "FACE_MODALITY":
                        if (!faceInfo) {
                            try {
                                const res = await apisState.BiometricDataApi.getBiometricFaceDataByUUID({ uuid });
                                if (isMounted) {
                                    setFaceInfo(res.data ?? null);
                                }
                            } catch (err: any) {
                                if (isMounted) {
                                    setFaceInfo(null);
                                    enqueueSnackbar(err.status === 404 ? "Image not found" : err.message, { 
                                        variant: err.status === 404 ? "warning" : "error" 
                                    });
                                }
                            } finally {
                                navigationDispatch(setLoading(false));
                            }
                        }
                        break;
                    case "VOICE_MODALITY":
                        if (!voiceInfo) {
                            try {
                                const res = await apisState.BiometricDataApi.getBiometricVoiceDataByUUID({ uuid });
                                if (isMounted) {
                                    setVoiceInfo(res.data ?? null);
                                }
                            } catch (err: any) {
                                if (isMounted) {
                                    setVoiceInfo(null);
                                    enqueueSnackbar(err.status === 404 ? "Audio not found" : err.message, { 
                                        variant: err.status === 404 ? "warning" : "error" 
                                    });
                                }
                            } finally {
                                navigationDispatch(setLoading(false));
                            }
                        }
                        break;
                    case "FINGERS_MODALITY":
                        if (fingerInfo.length === 0) {
                            if (fingers) {
                                const results = await Promise.all(
                                    fingers?.map(async (f) => {
                                        if (f.uuid) {
                                            return (await (apisState.BiometricDataApi.getBiometricFingerDataByUUID({ uuid: f.uuid }))).data;
                                        }
                                    })
                                );
                                setFingerInfo(results)
                            } else {
                                setFingerInfo([])
                            }
                        }
                        break;
                    default:
                        enqueueSnackbar("Modality not found", { variant: "error" });
                        break;
                }
            } catch (error: any) {
                enqueueSnackbar(error.message, { variant: "error" });
            } finally {
                navigationDispatch(setLoading(false));
            }
        };
    
        if (props.open) {
            fetchData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeModalityIndex, apisState.BiometricDataApi, enqueueSnackbar, navigationDispatch, operationList]);

    const handleDownload = (downloadOptions: MultimodalDownloadOption[]) => {
        if (props.multimodalSubject?.subjectId) {
            props.onImageDownload(props.multimodalSubject?.subjectId, downloadOptions)
        } else if (props.singleOperation?.uuid) {
            props.onImageDownload(props.singleOperation.uuid, downloadOptions)    
        } else {
            enqueueSnackbar("Can't get operation id", {variant: "error"})
        }      
    }

    const getImageArrowSelection = (left: boolean) => {
        return (
            operationList.length > 1?
                <SelectionIconButton
                    onClick={left? () => {setActiveModalityIndex(p => p + 1)} : () => {setActiveModalityIndex(p => p - 1)}}
                    disabled={left? activeModalityIndex + 1 >= operationList.length : activeModalityIndex <= 0}
                    style={{right: left? "15px" : undefined, left: left? undefined : "15px", position: "static", marginLeft: "0.5rem"}}
                    sx={{"&.Mui-disabled": {backgroundColor: "#e8e8e8"}}}
                    disableRipple
                >
                    <ArrowBackIosNewIcon style={{color: THEME.palette.text.secondary, transform: left? "scale(-1)" : "scale(1)"}} />
                </SelectionIconButton >
            : null
        )
    }

    const getSelectArrowBox = () => {
        return (
            <Box>
                {getImageArrowSelection(false)}
                {getImageArrowSelection(true)}
            </Box>
        )
    }

    return (
        <Dialog
            maxWidth="md"
            scroll="body"
            fullWidth
            fullScreen={isSmall ? true : false} 
            open={props.open}
            onClose={props.onClose}
            onTransitionExited={handleExited}
            PaperProps={{sx: {margin: "0rem"}}}
        >
            <DialogTitle>
                <DialogTitleClose
                    title={props.isOperation? "Operation's information" : "Subject's information"}
                    onClose={props.onClose}
                />
            </DialogTitle>
            <DialogContent style={{paddingBottom: "0px"}}> 
                <Grid container>
                    {operationList[activeModalityIndex]? 
                    <>
                        <GridImage item xs={12} md={5}>
                            {
                                operationList[activeModalityIndex].modality === "FACE_MODALITY" ?
                                    <ImageCanvas
                                        image={faceInfo}
                                        clickable
                                        rectangle={operationList[activeModalityIndex].boundingRect}
                                        subjectId={(operationList[activeModalityIndex] as Subject).subjectId ?? "None"}
                                    /> :
                                operationList[activeModalityIndex].modality === "VOICE_MODALITY" ?
                                    <AudioOperationSummary
                                        audioInfo={voiceInfo}
                                    /> :
                                operationList[activeModalityIndex].modality === "FINGERS_MODALITY" ?
                                    <ImageCanvas
                                        image={fingerInfo[fingerIndex]}
                                        clickable
                                        rectangle={operationList[activeModalityIndex].fingers?.[fingerIndex]?.fingerRectangles}
                                        subjectId={(operationList[activeModalityIndex] as Subject).subjectId ?? "None"}
                                        fingerCanvas={{
                                            max: operationList[activeModalityIndex]?.fingers?.length ?? 0,
                                            fingerIndex: fingerIndex,
                                            setFingerIndex: (number) => setFingerIndex(number) 
                                        }}
                                    />
                                    :
                                    null
                            }
                        </GridImage>
                        <Grid item xs={12} md={7}>
                            <OverviewInfo
                                modality={operationList[activeModalityIndex].modality}
                                operation={operationList[activeModalityIndex]}
                                isOperation={props.isOperation}
                                subjectId={props.multimodalSubject? props.multimodalSubject.subjectId ?? undefined : undefined}
                                qualities={operationList[activeModalityIndex]?.fingers?.[fingerIndex].qualities}
                                finger={operationList[activeModalityIndex]?.fingers?.[fingerIndex]}
                            />
                        </Grid>
                    </> : null
                    }
                </Grid> 
            </DialogContent>
            <DialogActions style={{display: "flex"}}>
                {props.isDeletable?
                    <ConfirmationButton
                        icon={<DeleteIcon />}
                        buttonTitle="Delete"
                        title="Confirmation"
                        text="Are you sure you want to delete selected subject(s)? All subject information will be removed and this action cannot be undone."
                        cancelText="Cancel"
                        confirmText="Delete"
                        confirm={props.onImageDelete}
                        confirmColor='error'
                    />
                    : null}
                <Box style={{ flex: '1 0 0' }} />
                {
                    props.isOperation && operationList[activeModalityIndex]?.uuid !== undefined &&
                    (operationList[activeModalityIndex]?.uuid as string).length > 0 &&
                    (operationList[activeModalityIndex].operationType === OperationType.CREATE_TEMPLATE || operationList[activeModalityIndex].operationType === OperationType.REEXTRACT_TEMPLATE) &&
                    operationList[activeModalityIndex].status === Status.SUCCESS?
                    <ButtonLightGray
                        onClick={() => {
                            navigationDispatch(setSubjectUUID(operationList[activeModalityIndex]?.uuid as string))
                            history.push("/subjects")
                        }}
                    >
                        Go to Subject
                    </ButtonLightGray> : null
                }
                {
                    props.isOperation && operationList[activeModalityIndex]?.uuid !== undefined &&
                    (operationList[activeModalityIndex]?.uuid as string).length > 0 &&
                    operationList[activeModalityIndex].operationType === OperationType.VERIFY &&
                    operationList[activeModalityIndex].subjectTableId !== 0 &&
                    operationList[activeModalityIndex].subjectTableId !== undefined?
                    <ButtonLightGray
                        onClick={() => {
                            apisState.SubjectsApi.getMultimodalSubjectById({id: (operationList[activeModalityIndex].subjectTableId as number)}).then(res => {
                                if (res.data.multimodalOperations !== undefined && res.data.multimodalOperations[0].uuid !== undefined ) {
                                    navigationDispatch(setSubjectUUID(res.data.multimodalOperations[0].uuid as string))
                                    history.push("/subjects")
                                }
                            }).catch(e => {console.error(e)})
                        }}
                    >
                        Go to Subject
                    </ButtonLightGray> : null
                }
                {isSmall ? null : getSelectArrowBox()}
                <BiometricDataDownload
                    onDownloadClick={handleDownload}
                    single
                    faceImageExists={faceInfo?.image !== undefined && faceInfo.image.length > 0}
                    faceTemplateExists={faceInfo?.template !== undefined && faceInfo.template.length > 0}
                    faceTokenImageExists={faceInfo?.tokenImage !== undefined && faceInfo.tokenImage.length > 0}
                    voiceAudioExists={voiceInfo?.audio !== undefined && voiceInfo.audio.length > 0}
                    voiceTemplateExists={voiceInfo?.template !== undefined && voiceInfo.template.length > 0}
                />
            </DialogActions>
            {isSmall ? 
                <DialogActions style={{display: "flex", justifyContent: "center"}}>
                    {getSelectArrowBox()}
                </DialogActions>
            : null}
        </Dialog>
    )
}

export default OverviewDialog
